import { resolveId } from '@medplum/core'
import { differenceInCalendarDays, parseISO } from 'date-fns'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { DEFAULT_CLINIC_TIME_ZONE } from '#src/addressTimezone.ts'
import {
  daysUntilCutoff,
  dueDateBeforeCutoff,
  nextCutoff,
} from '#src/cycleCalendar2026.ts'
import { isVaccineMedication } from '#src/medication-refill/isVaccineMedication.ts'
import type { ExtendedMedicationRequest } from '#src/models/medicationRequest.ts'

// GraneRx typically dispenses in fixed 28-day packs. The clock is the pack the pharmacy
// most recently reported (cycleStart, ORC.15) — not occurredAt or ZPI.10. See
// thoughts/shared/tasks/med-refill-tasking/00-master-summary.md.
const PACK_LENGTH_DAYS = 28
export const HORIZON_DAYS = 28
export const URGENT_WITHIN_DAYS = 3
// Supply that ended before tasking shipped was handled by the manual processes
// it replaces. Also stops a stale order filing a task every cycle forever — the
// horizon test only bounds how far ahead supply may end.
export const IGNORE_SUPPLY_ENDED_BEFORE_DAY = '2026-10-01'

export interface RefillMedLine {
  medicationRequestId: string
  participantId: string
  display: string
  supplyEnds: Date
}

export interface RefillTaskDraft {
  participantId: string
  cutoff: Date
  dueOn: Date
  title: string
  meds: RefillMedLine[]
}

export interface RefillVisitNoteLine {
  participantId: string
  meds: RefillMedLine[]
}

export type RefillSkipReason =
  | 'missing_id'
  | 'superseded'
  | 'missing_participant'
  | 'prn_refills_not_zero'
  | 'prn_no_visit'
  | 'inactive'
  | 'vaccine'
  | 'no_granerx_blob'
  | 'refills_remaining'
  | 'negative_refills_remaining'
  | 'topical'
  | 'no_refills_authorized'
  | 'temporary'
  | 'order_control_nw'
  | 'missing_cycle_start'
  | 'missing_refills_remaining'
  | 'supply_ended_before_cutoff'
  | 'outside_horizon'

export interface RefillDecisionStats {
  noNextCutoff: boolean
  includedOnTask: number
  deferredToVisitNote: number
  listedOnVisitNote: number
  prnListedOnVisitNote: number
  skipped: Partial<Record<RefillSkipReason, number>>
}

export interface RefillDecision {
  tasks: readonly RefillTaskDraft[]
  visitNoteLines: readonly RefillVisitNoteLine[]
  stats: RefillDecisionStats
}

export function emptyRefillDecisionStats(
  overrides: Partial<RefillDecisionStats> = {}
): RefillDecisionStats {
  return {
    noNextCutoff: false,
    includedOnTask: 0,
    deferredToVisitNote: 0,
    listedOnVisitNote: 0,
    prnListedOnVisitNote: 0,
    skipped: {},
    ...overrides,
  }
}

function addLine(
  map: Map<string, RefillMedLine[]>,
  participantId: string,
  line: RefillMedLine
): void {
  const lines = map.get(participantId) ?? []
  lines.push(line)
  map.set(participantId, lines)
}

function countSkip(stats: RefillDecisionStats, reason: RefillSkipReason): void {
  stats.skipped[reason] = (stats.skipped[reason] ?? 0) + 1
}

// Pack math runs on civil dates, never on instants. `cycleStart` (ORC.15) is
// date-only stored as a UTC-midnight instant, so its civil date is read in UTC;
// `today` is a real instant, so its civil date is read in the clinic zone. Doing
// this in `Date` arithmetic instead makes the horizon shift by a day with the
// host time zone, because the two operands are built in different frames.
function civilDay(instant: Date, timeZone: string): string {
  return formatInTimeZone(instant, timeZone, 'yyyy-MM-dd')
}

function addCivilDays(day: string, days: number): string {
  const [year, month, date] = day.split('-').map(Number)
  return civilDay(new Date(Date.UTC(year, month - 1, date + days)), 'UTC')
}

function civilDaysBetween(fromDay: string, toDay: string): number {
  return differenceInCalendarDays(
    new Date(`${toDay}T00:00:00Z`),
    new Date(`${fromDay}T00:00:00Z`)
  )
}

// Start of that clinic day — the instant the supply is gone.
function clinicMidnight(day: string): Date {
  return fromZonedTime(`${day}T00:00:00`, DEFAULT_CLINIC_TIME_ZONE)
}

// Pure decision function: given active medications and own-PCP visit windows,
// decide which participants need a refill task filed and which cycle / PRN
// meds belong on the visit-note list instead. Callers must keep day-center
// appointments out of `ownPcpVisitsByParticipant`. An empty visit map is
// still valid — every in-horizon cycle med becomes a task.
//
// Visit-note deferral is per run, not sticky. Cron rebuilds the visit map
// from booked appointments starting today-or-later. If yesterday's visit
// happened without a refill, or the suppressing appointment was cancelled,
// the same cycle med becomes a task on the next daily run.
//
// `listForVisitNote` is the note-widget path. The note is already the visit,
// so skip appointment routing: every eligible cycle med and every remaining-0
// PRN goes on visitNoteLines, and tasks is empty. Cron / pilot keep the
// default (route by visit). Once a GraneRx order is present, refills remaining
// is one split on both paths, before topical and pack math: above zero
// (`refills_remaining`), missing (`missing_refills_remaining`), or exactly zero.
// A negative count does not make sense and returns as `negative_refills_remaining`
// before topical and pack math. The widget lists a topical only at exactly zero,
// with refills authorized, on a permanent, already-filled order.
export function decideRefillActions(input: {
  // The run instant. Its clinic-local civil day anchors the horizon, so pass a
  // real `new Date()` — a host-local midnight resolves to the wrong clinic day
  // outside Pacific.
  today: Date
  medications: readonly ExtendedMedicationRequest[]
  ownPcpVisitsByParticipant: ReadonlyMap<string, { start: Date }>
  // Defaults to IGNORE_SUPPLY_ENDED_BEFORE_DAY at clinic midnight.
  ignoreSupplyEndedBefore?: Date
  horizonDays?: number
  listForVisitNote?: boolean
}): RefillDecision {
  const {
    today,
    medications,
    ownPcpVisitsByParticipant,
    ignoreSupplyEndedBefore = clinicMidnight(IGNORE_SUPPLY_ENDED_BEFORE_DAY),
    horizonDays = HORIZON_DAYS,
    listForVisitNote = false,
  } = input

  const cutoffRow = nextCutoff(today)
  if (!cutoffRow) {
    return {
      tasks: [],
      visitNoteLines: [],
      stats: emptyRefillDecisionStats({ noNextCutoff: true }),
    }
  }
  const { cutoff } = cutoffRow
  const dueOn = dueDateBeforeCutoff(cutoff)
  const daysLeft = daysUntilCutoff(today, cutoff)
  const todayDay = civilDay(today, DEFAULT_CLINIC_TIME_ZONE)
  const title =
    daysLeft < URGENT_WITHIN_DAYS ? '! Medication refill' : 'Medication refill'

  // A request superseded by a later active request in this same set (its
  // priorPrescription points back to it) is not the one to refill.
  const supersededIds = new Set(
    medications
      .filter((med) => med.status === 'active')
      .map((med) => resolveId(med.priorPrescription))
      .filter((id): id is string => Boolean(id))
  )

  const taskLinesByParticipant = new Map<string, RefillMedLine[]>()
  const visitLinesByParticipant = new Map<string, RefillMedLine[]>()
  const stats = emptyRefillDecisionStats()

  for (const med of medications) {
    if (!med.id) {
      countSkip(stats, 'missing_id')
      continue
    }
    if (supersededIds.has(med.id)) {
      countSkip(stats, 'superseded')
      continue
    }
    const participantId = resolveId(med.subject)
    if (!participantId) {
      countSkip(stats, 'missing_participant')
      continue
    }

    if (med.status !== 'active') {
      countSkip(stats, 'inactive')
      continue
    }

    // Before PRN routing so a remaining-zero PRN vaccine stays off the widget.
    if (isVaccineMedication(med)) {
      countSkip(stats, 'vaccine')
      continue
    }

    if (med.isPrn) {
      if (med.refillsRemaining !== 0) {
        countSkip(stats, 'prn_refills_not_zero')
        continue
      }
      if (!listForVisitNote && !ownPcpVisitsByParticipant.has(participantId)) {
        countSkip(stats, 'prn_no_visit')
        continue
      }
      addLine(visitLinesByParticipant, participantId, {
        medicationRequestId: med.id,
        participantId,
        display: med.medicationName ?? 'Medication',
        // PRN has no pack clock; remaining-zero is the trigger.
        supplyEnds: today,
      })
      stats.prnListedOnVisitNote += 1
      continue
    }

    // Refill counts are only read once GraneRx owns the order. A local-pharmacy
    // fill has no blob, so it stays `no_granerx_blob` and never hits this gate.
    if (!med.granerxOrder) {
      countSkip(stats, 'no_granerx_blob')
      continue
    }
    // One split, before topical and pack math. Missing, negative, and above
    // zero leave here. Exactly zero continues. A negative count is not a
    // remaining refill. GraneRx can still dispense a count above zero.
    const remaining = med.refillsRemaining
    if (remaining === undefined) {
      countSkip(stats, 'missing_refills_remaining')
      continue
    }
    if (remaining < 0) {
      countSkip(stats, 'negative_refills_remaining')
      continue
    }
    if (remaining > 0) {
      countSkip(stats, 'refills_remaining')
      continue
    }

    const cycleSkip = cycleSkipReason(med)
    // Widget only. An exactly-zero cycle topical is shown like a remaining-zero
    // PRN: no pack math. It has already passed the authorized, temporary, and
    // first-fill gates, which run before the topical gate. Tasking still skips
    // every topical.
    if (cycleSkip === 'topical' && listForVisitNote && remaining === 0) {
      addLine(visitLinesByParticipant, participantId, {
        medicationRequestId: med.id,
        participantId,
        display: med.medicationName ?? 'Medication',
        supplyEnds: today,
      })
      stats.listedOnVisitNote += 1
      continue
    }
    if (cycleSkip) {
      countSkip(stats, cycleSkip)
      continue
    }
    const cycleStart = med.cycleStart
    if (!cycleStart) {
      countSkip(stats, 'missing_cycle_start')
      continue
    }

    const supplyEndsDay = addCivilDays(
      civilDay(parseISO(cycleStart), 'UTC'),
      PACK_LENGTH_DAYS * (1 + remaining)
    )
    const supplyEnds = clinicMidnight(supplyEndsDay)

    if (supplyEnds < ignoreSupplyEndedBefore) {
      countSkip(stats, 'supply_ended_before_cutoff')
      continue
    }
    if (civilDaysBetween(todayDay, supplyEndsDay) > horizonDays) {
      countSkip(stats, 'outside_horizon')
      continue
    }

    const line: RefillMedLine = {
      medicationRequestId: med.id,
      participantId,
      display: med.medicationName ?? 'Medication',
      supplyEnds,
    }
    if (listForVisitNote) {
      addLine(visitLinesByParticipant, participantId, line)
      stats.listedOnVisitNote += 1
      continue
    }
    const visit = ownPcpVisitsByParticipant.get(participantId)
    if (visit && visit.start < supplyEnds && daysLeft >= URGENT_WITHIN_DAYS) {
      addLine(visitLinesByParticipant, participantId, line)
      stats.deferredToVisitNote += 1
    } else {
      addLine(taskLinesByParticipant, participantId, line)
      stats.includedOnTask += 1
    }
  }

  const tasks: RefillTaskDraft[] = Array.from(
    taskLinesByParticipant.entries()
  ).map(([participantId, meds]) => ({
    participantId,
    cutoff,
    dueOn,
    title,
    meds,
  }))

  const visitNoteLines: RefillVisitNoteLine[] = Array.from(
    visitLinesByParticipant.entries()
  ).map(([participantId, meds]) => ({
    participantId,
    meds,
  }))

  return { tasks, visitNoteLines, stats }
}

// Identity of a cycle med, before pack math. Horizon, supply-ended-before,
// supersession, visit routing, and refills still on file decide whether to
// file a task, not whether the med is a cycle med. Active is checked here
// because cycleSkipReason no longer does. Topicals fail cycleSkipReason, so
// they are not cycle meds.
export function isCycleMedication(med: ExtendedMedicationRequest): boolean {
  if (med.status !== 'active') {
    return false
  }
  if (isVaccineMedication(med)) {
    return false
  }
  if (med.isPrn) {
    return false
  }
  if (cycleSkipReason(med)) {
    return false
  }
  if (med.refillsRemaining === undefined) {
    return false
  }
  if (!med.cycleStart) {
    return false
  }
  return true
}

// Cycle skip: GraneRx-tracked, has authorized refills, not a one-time/temporary
// order, not a brand-new (unfilled) order, and not topical. Active is
// checked by the caller before PRN routing. The pack start is checked inline
// by the caller, which needs the value itself. PRN is routed before this
// helper is called.
function cycleSkipReason(
  med: ExtendedMedicationRequest
): RefillSkipReason | undefined {
  if (!med.granerxOrder) {
    return 'no_granerx_blob'
  }
  if (!med.refillsAuthorized || med.refillsAuthorized <= 0) {
    return 'no_refills_authorized'
  }
  if (med.isPermanent === false) {
    return 'temporary'
  }
  if (med.granerxOrder.orderControl === 'NW') {
    return 'order_control_nw'
  }
  if (med.isTopical) {
    return 'topical'
  }
  return undefined
}
